import React, { useMemo, useState, useEffect, useCallback } from "react";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "./Home.css";
import "./GroceriesHome.css";
import { useDispatch } from 'react-redux';
import { addItem, delItem } from '../redux/action/index';
import { sendRequest } from "../ApiRequest";
import { sendCachedRequest } from "../utils/apiCache";
import { BASE_URL } from "../URL/constants";
import getCurrentUser from "../components/common/GetCurrentUser";
import { useLocation, useNavigate } from "react-router-dom";
import useHtmlLinkNavigation from "../hooks/useHtmlLinkNavigation";
import Login from "./buttons/Login";
import SignupModal from "./buttons/SignupModal";
import AddToCartButton from "../components/common/AddToCartButton";
import { isItemOutOfStock } from '../utils/stockUtils';
import CheckoutHandler from "./common/CheckoutHandler";
import { addGuestCartItem, removeGuestCartItem } from "../utils/guestCart";

const formatPrice = (value = 0) => `₹${Number(value || 0).toFixed(2)}`;

const getDiscountedPrice = (product) =>
  product.sales_discount > 0
    ? Math.max(product.sales_price - product.sales_discount, 0)
    : product.sales_price;

const getDiscountPercent = (product) => {
  if (!product.sales_discount || !product.sales_price) return 0;
  return Math.round((product.sales_discount / product.sales_price) * 100);
};

const GroceriesHome = ({ 
  carouselHtml = [],
  tagline = "",
  companyName = "",
  bodyHtml = []
}) => {
  const [hoveredProduct, setHoveredProduct] = useState(null);
  const [allProducts, setAllProducts] = useState([]);
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeCategory, setActiveCategory] = useState("ALL PRODUCTS");
  const [categories, setCategories] = useState([]);
  const [navLoading, setNavLoading] = useState(true);
  const [showAllCategories, setShowAllCategories] = useState(false);
  const { isLoggedIn, currentUser } = getCurrentUser();
  const [quantity, setQuantity] = useState(1);

  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const [pendingCheckoutItem, setPendingCheckoutItem] = useState(null);
  
  // SignupModal states
  const [signupModalOpen, setSignupModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("login");
  const [showOtpLogin, setShowOtpLogin] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [otp, setOtp] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const bodyContentRef = useHtmlLinkNavigation(bodyHtml);

  const defaultImg = "https://via.placeholder.com/300x300?text=No+Image";

  const visibleCategories = showAllCategories ? categories : categories.slice(0, 4);

  // Carousel settings
  const settings = {
    dots: false,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 4000,
    arrows: true,
    fade: true,
  };

  const fetchCategories = useCallback(async () => {
    try {
      setNavLoading(true);
      const response = await sendCachedRequest(
        "GET",
        `${BASE_URL}/v1/public_site/menus/fetch_categories`,
        null,
        { cacheKey: "menu_categories" }
      );
      
      const allProductsCategory = { 
        id: "all", 
        name: "ALL PRODUCTS" 
      };

      setCategories([allProductsCategory, ...response.data.categories]);
      setActiveCategory(allProductsCategory);
    } catch (error) {
      console.error("Failed to fetch categories:", error);
      setError("Failed to load navigation. Please try again later.");
    } finally {
      setNavLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const fetchAllProducts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await sendCachedRequest(
        "GET",
        `${BASE_URL}/v1/public_site/menus/item_lists?is_favourite=true&per_page=20`,
        null,
        { cacheKey: "item_lists_all" }
      );

      // Transform API data to match our structure
      const products = response.data.items.map((item) => ({
        id: item.id,
        item_name: item.item_name,
        sales_price: item.sales_price,
        sales_discount: item.sales_discount || 0,
        quantity: item.quantity,
        image: item.item_images?.[0] || defaultImg,
        category: item.category || "ALL PRODUCTS",
      }));

      setAllProducts(products);
      setFeaturedProducts(products);
    } catch (err) {
      console.error("Failed to fetch products:", err);
      setError("Failed to load products. Please try again later.");
      setAllProducts([]);
      setFeaturedProducts([]);
    } finally {
      setLoading(false);
    }
  }, [defaultImg]);

  const fetchProductsByCategory = async (categoryId) => {
    try {
      setLoading(true);
      setError(null);

      if (categoryId === "all") {
        setFeaturedProducts(allProducts);
        setLoading(false);
        return;
      }
      const response = await sendCachedRequest(
        "GET",
        `${BASE_URL}/v1/public_site/menus/item_lists?is_favourite=true&per_page=20`,
        null,
        {
          params: { category_id: categoryId, per_page: 20 },
          cacheKey: `item_lists_category_${categoryId}`,
        }
      );

      // Transform API data to match our structure
      const products = response.data.items.map((item) => ({
        id: item.id,
        item_name: item.item_name,
        sales_price: item.sales_price,
        sales_discount: item.sales_discount || 0,
        quantity: item.quantity,
        image: item.item_images?.[0] || defaultImg,
        category: item.category || "",
      }));

      setFeaturedProducts(products);
    } catch (err) {
      console.error("Failed to fetch products by category:", err);
      setError("Failed to load products. Please try again later.");
      setFeaturedProducts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllProducts();
  }, [fetchAllProducts]);

  const handleCategoryClick = (category) => {
    setActiveCategory(category);
    if (category.id === "all") {
      // Navigate to products page without category filter
      navigate('/products');
    } else {
      // Navigate to products page with category filter
      navigate(`/products?category=${encodeURIComponent(category.name)}`);
    }
  };

  const goToProductDetail = (productId) => {
    navigate(`/products/${productId}`);
  };

  return (
    <div className="home-page groceries-home">
      {/* Hero Carousel Section */}
      {carouselHtml.length > 0 && (
        <div className="carousel-wrapper salon-carousel-wrapper">
          <Slider {...settings}>
            {carouselHtml.map((image, index) => (
              <div key={`carousel-${index}`} className="carousel-slide">
                <img
                  src={image}
                  alt={`Slide ${index + 1}`}
                  className="carousel-image"
                />
              </div>
            ))}
          </Slider>
        </div>
      )}

      {/* Tagline Section */}
      {tagline && (
        <div className="tagline-section">
          <h2>{tagline}</h2>
          {companyName && <p className="company-subtitle">Welcome to {companyName}</p>}
        </div>
      )}

      {/* Category Navigation */}
      <div className="category-navigation">
        {navLoading ? (
          <div className="loading">Loading categories...</div>
        ) : (
          <>
            <div className="category-buttons">
              {visibleCategories.map((category) => (
                <button
                  key={category.id}
                  className={`category-btn ${
                    activeCategory.id === category.id ? "active" : ""
                  }`}
                  onClick={() => handleCategoryClick(category)}
                >
                  {category.name}
                </button>
              ))}
            </div>
            {categories.length > 4 && (
              <button
                className="show-more-btn"
                onClick={() => setShowAllCategories(!showAllCategories)}
              >
                {showAllCategories ? "Show Less" : "Show More"}
              </button>
            )}
          </>
        )}
      </div>

      <div className="products-section">
        {loading ? (
          <div className="loading">Loading products...</div>
        ) : error ? (
          <div className="error">{error}</div>
        ) : featuredProducts.length === 0 ? (
          <div className="no-products">No products available</div>
        ) : featuredProducts.length > 4 ? (
          <div className="products-slider-container">
            <Slider
              dots={true}
              infinite={false}
              speed={500}
              slidesToShow={Math.min(4, featuredProducts.length)}
              slidesToScroll={1}
              arrows={true}
              swipeToSlide={true}
              draggable={true}
              responsive={[
                {
                  breakpoint: 1200,
                  settings: {
                    slidesToShow: Math.min(3, featuredProducts.length),
                    slidesToScroll: 1,
                  },
                },
                {
                  breakpoint: 992,
                  settings: {
                    slidesToShow: Math.min(3, featuredProducts.length),
                    slidesToScroll: 1,
                  },
                },
                {
                  breakpoint: 768,
                  settings: {
                    slidesToShow: 2,
                    slidesToScroll: 1,
                  },
                },
                {
                  breakpoint: 480,
                  settings: {
                    slidesToShow: 1,
                    slidesToScroll: 1,
                  },
                },
              ]}
            >
              {featuredProducts.map((product, index) => {
                const discountedPrice = getDiscountedPrice(product);
                const discountPercent = getDiscountPercent(product);
                const isOutOfStock = isItemOutOfStock(product);

                return (
                  <div key={`${product.id}-${index}`} className="product-slide">
                    <div
                      className="product-card"
                      onMouseEnter={() => setHoveredProduct(product.id)}
                      onMouseLeave={() => setHoveredProduct(null)}
                    >
                      <div className="product-image-container">
                        {discountPercent > 0 && (
                          <div className="discount-badge">
                            {discountPercent}% OFF
                          </div>
                        )}
                        <img
                          src={product.image}
                          alt={product.item_name}
                          className="product-image"
                          onClick={() => goToProductDetail(product.id)}
                        />
                      </div>
                      <div className="product-info">
                        <h3
                          className="product-name"
                          onClick={() => goToProductDetail(product.id)}
                        >
                          {product.item_name}
                        </h3>
                        <div className="product-pricing">
                          {product.sales_discount > 0 && (
                            <span className="original-price">
                              {formatPrice(product.sales_price)}
                            </span>
                          )}
                          <span className="current-price">
                            {formatPrice(discountedPrice)}
                          </span>
                        </div>
                        <AddToCartButton
                          product={product}
                          quantity={quantity}
                          isOutOfStock={isOutOfStock}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </Slider>
          </div>
        ) : (
          <div className="products-grid">
            {featuredProducts.map((product, index) => {
              const discountedPrice = getDiscountedPrice(product);
              const discountPercent = getDiscountPercent(product);
              const isOutOfStock = isItemOutOfStock(product);

              return (
                <div
                  key={`${product.id}-${index}`}
                  className="product-card"
                  onMouseEnter={() => setHoveredProduct(product.id)}
                  onMouseLeave={() => setHoveredProduct(null)}
                >
                  <div className="product-image-container">
                    {discountPercent > 0 && (
                      <div className="discount-badge">
                        {discountPercent}% OFF
                      </div>
                    )}
                    <img
                      src={product.image}
                      alt={product.item_name}
                      className="product-image"
                      onClick={() => goToProductDetail(product.id)}
                    />
                  </div>
                  <div className="product-info">
                    <h3
                      className="product-name"
                      onClick={() => goToProductDetail(product.id)}
                    >
                      {product.item_name}
                    </h3>
                    <div className="product-pricing">
                      {product.sales_discount > 0 && (
                        <span className="original-price">
                          {formatPrice(product.sales_price)}
                        </span>
                      )}
                      <span className="current-price">
                        {formatPrice(discountedPrice)}
                      </span>
                    </div>
                    <AddToCartButton
                      product={product}
                      quantity={quantity}
                      isOutOfStock={isOutOfStock}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {bodyHtml && (
        <div className="body-content-section">
          {Array.isArray(bodyHtml) ? (
            bodyHtml.map((content, index) => (
              <div key={index} dangerouslySetInnerHTML={{ __html: content }} />
            ))
          ) : (
            <div dangerouslySetInnerHTML={{ __html: bodyHtml }} />
          )}
        </div>
      )}

      <Login
        isOpen={loginModalOpen}
        onClose={() => setLoginModalOpen(false)}
        onLoginSuccess={() => {
          setLoginModalOpen(false);
          if (pendingCheckoutItem) {
            const { handleCheckout } = CheckoutHandler({
              items: [pendingCheckoutItem],
              setShowLoginModal: setLoginModalOpen,
              navigate,
              dispatch,
              isLoggedIn: true,
            });
            handleCheckout();
            setPendingCheckoutItem(null);
          }
        }}
      />

      <SignupModal
        isOpen={signupModalOpen}
        onClose={() => setSignupModalOpen(false)}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        showOtpLogin={showOtpLogin}
        setShowOtpLogin={setShowOtpLogin}
        phoneNumber={phoneNumber}
        setPhoneNumber={setPhoneNumber}
        otp={otp}
        setOtp={setOtp}
        firstName={firstName}
        setFirstName={setFirstName}
        lastName={lastName}
        setLastName={setLastName}
        email={email}
        setEmail={setEmail}
      />
    </div>
  );
};

export default GroceriesHome;

